import { z } from "zod";
import { ufSchema } from "./calculation.js";
import { calculatorResearchBatchCreateSchema, calculatorResearchStatusSchema, } from "./calculator-research.js";
import { coverageGapSchema } from "./coverage-gaps.js";
import { publishedRulesetReferenceSchema } from "./published-rule-matching.js";
export const spRouteDirectionSchema = z.enum(["ALL_TO_SP", "SP_TO_ALL"]);
export const spRouteQualificationSchema = z.enum(["COMPLETE", "PARTIAL", "MISSING", "BLOCKED"]);
export const spRouteCheckIdSchema = z.enum([
    "DECLARED_PRODUCT_SCOPE",
    "APPLICABLE_RULES",
    "SOURCE_PROVENANCE",
    "SOURCE_CURRENTNESS",
    "SUPPLIER_CASES",
    "VALIDATION_RECEIPTS",
]);
export const spRouteCheckSchema = z
    .object({
    id: spRouteCheckIdSchema,
    status: z.enum(["SATISFIED", "PENDING", "FAILED"]),
    reason: z.string().min(1).max(120),
})
    .strict();
export const spRouteCoverageEntrySchema = z
    .object({
    uf: ufSchema,
    originUf: ufSchema,
    destinationUf: ufSchema,
    qualification: spRouteQualificationSchema,
    displayStatus: z.enum(["COMPLETE", "PARTIAL", "MISSING", "BLOCKED", "RUNNING"]),
    applicableRuleCount: z.number().int().nonnegative(),
    productCount: z.number().int().nonnegative(),
    boundEvidenceCount: z.number().int().nonnegative(),
    checks: z.array(spRouteCheckSchema).length(6),
    activeResearchCount: z.number().int().nonnegative(),
    runningResearchCount: z.number().int().nonnegative(),
    blockedResearchCount: z.number().int().nonnegative(),
    research: z
        .array(z
        .object({
        id: z.string().uuid(),
        ncm: z.string().regex(/^\d{8}$/u),
        cest: z.string().nullable(),
        status: calculatorResearchStatusSchema,
        step: z.string().min(1).max(30),
        blocked: z.string().nullable(),
        errorCode: z.string().nullable(),
        updatedAt: z.string().datetime(),
    })
        .strict())
        .max(50),
    researchTruncated: z.boolean(),
    ruleSamples: z
        .array(z
        .object({
        id: z.string().uuid(),
        ncm: z.string(),
        cest: z.string().nullable(),
        validFrom: z.string().date(),
        validTo: z.string().date().nullable(),
        evidence: z
            .array(z
            .object({
            id: z.string().uuid(),
            sha256: z.string().regex(/^[a-f0-9]{64}$/u),
            source: z.string().min(1),
            validFrom: z.string().date().nullable(),
            validTo: z.string().date().nullable(),
        })
            .strict())
            .max(5),
    })
        .strict())
        .max(10),
    rulesTruncated: z.boolean(),
    waitingReviewCount: z.number().int().nonnegative(),
    failedResearchCount: z.number().int().nonnegative(),
    gapCount: z.number().int().nonnegative(),
    gaps: z.array(coverageGapSchema).max(50),
    gapsTruncated: z.boolean(),
    availableSourceCount: z.number().int().nonnegative(),
    degradedSourceCount: z.number().int().nonnegative(),
})
    .strict()
    .superRefine((entry, context) => {
    const ids = new Set(entry.checks.map((check) => check.id));
    if (ids.size !== 6)
        context.addIssue({
            code: "custom",
            path: ["checks"],
            message: "Each required check must occur once",
        });
    if (entry.qualification === "COMPLETE" &&
        entry.checks.some((check) => check.status !== "SATISFIED")) {
        context.addIssue({
            code: "custom",
            path: ["qualification"],
            message: "Complete routes require all six qualification checks",
        });
    }
    if (entry.displayStatus === "RUNNING"
        ? entry.runningResearchCount === 0
        : entry.displayStatus !== entry.qualification &&
            !(entry.displayStatus === "BLOCKED" && entry.blockedResearchCount > 0)) {
        context.addIssue({
            code: "custom",
            path: ["displayStatus"],
            message: "Running is an execution overlay; otherwise show the qualification",
        });
    }
    if (!entry.gapsTruncated && entry.gapCount !== entry.gaps.length) {
        context.addIssue({
            code: "custom",
            path: ["gapCount"],
            message: "A complete gap listing must match its count",
        });
    }
});
/** This records qualification of a declared scope, never a national coverage percentage. */
export const spRouteCoverageSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    direction: spRouteDirectionSchema,
    centerUf: z.literal("SP"),
    checkedAsOf: z.string().date(),
    observedAt: z.string().datetime(),
    publishedRuleset: publishedRulesetReferenceSchema.nullable(),
    productScope: z.literal("PUBLISHED_FISCAL_RULES_ONLY"),
    productScopeQualified: z.boolean(),
    validationReceiptsAvailable: z.boolean(),
    routes: z.array(spRouteCoverageEntrySchema).length(27),
    execution: z
        .object({
        dispatcherConfigured: z.boolean(),
        provider: z.literal("EXISTING_CALCULATOR_RESEARCH"),
        budgetStatus: z.literal("REQUIRES_READ_ONLY_ESTIMATE"),
        runtimeHealth: z.literal("NOT_VERIFIED_BY_CONFIGURATION"),
        argon: z.literal("NOT_CONFIGURED"),
        autoPublication: z.literal(false),
    })
        .strict(),
})
    .strict()
    .superRefine((response, context) => {
    if (new Set(response.routes.map((route) => route.uf)).size !== 27)
        context.addIssue({
            code: "custom",
            path: ["routes"],
            message: "All 27 UFs must occur exactly once",
        });
    for (const route of response.routes) {
        const correct = response.direction === "ALL_TO_SP"
            ? route.originUf === route.uf && route.destinationUf === "SP"
            : route.originUf === "SP" && route.destinationUf === route.uf;
        if (!correct)
            context.addIssue({
                code: "custom",
                path: ["routes"],
                message: "Route does not belong to its SP direction",
            });
        if (route.qualification === "COMPLETE" &&
            (!response.productScopeQualified || !response.validationReceiptsAvailable))
            context.addIssue({
                code: "custom",
                path: ["routes"],
                message: "This read model has no qualified scope or validation receipts; it cannot certify COMPLETE",
            });
    }
});
/** Latest snapshot guard for NEW submissions; server proof remains authoritative. */
export function spCoverageSelectionCurrent(selected, snapshot) {
    if (!selected.length)
        return true;
    if (!snapshot)
        return false;
    return selected.every((original) => {
        const route = snapshot.routes.find((r) => r.originUf === original.originUf && r.destinationUf === original.destinationUf);
        const current = route?.gaps.find((g) => g.id === original.id);
        return Boolean(route &&
            !route.researchTruncated &&
            current &&
            current.status === "OPEN" &&
            current.revision === original.revision &&
            current.lastCalculationId === original.lastCalculationId &&
            current.lastCalculationDate === original.lastCalculationDate &&
            current.ncm === original.ncm &&
            current.cest === original.cest &&
            current.researchTaskStatus !== "QUEUED" &&
            current.researchTaskStatus !== "RUNNING" &&
            !route.research.some((r) => r.ncm === original.ncm &&
                r.cest === original.cest &&
                (r.status === "QUEUED" || r.status === "RUNNING")));
    });
}
/** Map explicitly selected, tenant-read gaps to the existing bounded research workflow. */
export function spCoverageResearchPlans(gaps, direction) {
    if (!gaps.length || gaps.length > 10 || new Set(gaps.map((gap) => gap.id)).size !== gaps.length)
        throw new RangeError("Select 1..10 distinct gaps");
    const groups = new Map();
    for (const value of gaps) {
        const gap = coverageGapSchema.parse(value);
        if ((direction === "ALL_TO_SP" ? gap.destinationUf !== "SP" : gap.originUf !== "SP") ||
            gap.status !== "OPEN" ||
            gap.researchTaskStatus === "QUEUED" ||
            gap.researchTaskStatus === "RUNNING")
            throw new RangeError("Gap is outside the selected direction or already active");
        const key = `${gap.lastCalculationId}:${gap.lastCalculationDate}`;
        const group = groups.get(key) ?? {
            origin: {
                kind: "CALCULATION",
                id: gap.lastCalculationId,
                calculationDate: gap.lastCalculationDate,
            },
            gaps: [],
            gapIds: [],
            selections: [],
        };
        group.gaps.push({
            ncm: gap.ncm,
            cest: gap.cest,
            originUf: gap.originUf,
            destinationUf: gap.destinationUf,
            families: ["ICMS", "ICMS_ST"],
            rowRefs: [],
        });
        group.gapIds.push(gap.id);
        group.selections.push({ id: gap.id, revision: gap.revision });
        groups.set(key, group);
    }
    return [...groups.values()].map((group) => ({
        gapIds: group.gapIds,
        body: calculatorResearchBatchCreateSchema.parse({
            schemaVersion: "1.0.0",
            mode: "ASK",
            origin: group.origin,
            force: false,
            gaps: group.gaps,
            coverageSelection: { direction, gaps: group.selections },
        }),
    }));
}
export const spRouteCoverageQuerySchema = z
    .object({
    direction: spRouteDirectionSchema.default("ALL_TO_SP"),
    checkedAsOf: z.string().date().optional(),
    ncm: z
        .string()
        .regex(/^\d{8}$/u)
        .optional(),
})
    .strict();
//# sourceMappingURL=sp-route-coverage.js.map